/** A signed-in session as the shell sees it. Never logged, never shown. */
export interface AuthSession {
  accessToken: string;
  /** Expiry in epoch milliseconds, when the token says. */
  expiresAt: number | null;
}

export type AuthState =
  | { status: 'loading' }
  | { status: 'signedOut'; reason?: 'expired' | 'rejected' }
  | { status: 'signedIn'; session: AuthSession }
  /** No sign-in method is configured for this build. */
  | { status: 'unconfigured' };

/**
 * How the user signs in. `development` takes a pasted or configured development token. `password` is the
 * placeholder email-and-password sign-in for the Development environment, until the identity provider (P1).
 */
export type AuthMethod = 'development' | 'password' | 'none';

/** What `signIn` takes: a development token, or an email and password for the placeholder sign-in. */
export type SignInInput = { token: string } | { email: string; password: string };

/**
 * What every sign-in implementation provides. The shell and the API client depend on this interface only, so the
 * identity provider, once chosen (W1, P1), replaces the development provider without touching either.
 */
export interface AuthProvider {
  method: AuthMethod;
  state: AuthState;
  /** A current access token, or null when signed out or expired. */
  getAccessToken: () => Promise<string | null>;
  /**
   * Starts sign-in. The development provider takes a token, the placeholder password provider an email and
   * password; a redirect provider ignores the input. Throws AuthError when sign-in is refused.
   */
  signIn: (input?: SignInInput) => Promise<void>;
  signOut: () => Promise<void>;
  /** Called by the API client on a 401: the token was refused, so the session ends. */
  sessionRejected: () => void;
}

export class AuthError extends Error {
  constructor(
    message: string,
    readonly code: 'malformed' | 'expired' | 'invalidCredentials' | 'throttled' | 'unavailable',
  ) {
    super(message);
    this.name = 'AuthError';
  }
}
